import React, { useState, useEffect } from 'react';
import chatService from '../services/chatService';
import RatingDetailsModal from './RatingDetailsModal';

// Shimmer skeleton component
const StatsWidgetSkeleton = () => {
  return (
    <div className="mt-4 bg-white rounded-lg shadow-lg p-6 animate-fade-in-up">
      {/* Header skeleton */}
      <div className="flex items-center gap-2 mb-4">
        <div className="w-8 h-8 bg-gray-200 rounded-full skeleton"></div>
        <div className="h-5 bg-gray-200 rounded skeleton w-40"></div>
      </div>

      {/* Stats grid skeleton */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Card 1 - Online users */}
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-4 rounded-lg border border-green-200">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-3 h-3 bg-gray-200 rounded-full skeleton"></div>
            <div className="h-4 bg-gray-200 rounded skeleton w-24"></div>
          </div>
          <div className="h-8 bg-gray-200 rounded skeleton w-16 mb-1"></div>
          <div className="h-3 bg-gray-200 rounded skeleton w-20"></div>
        </div>

        {/* Card 2 - Total replacements */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-4 rounded-lg border border-blue-200">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-4 h-4 bg-gray-200 rounded skeleton"></div>
            <div className="h-4 bg-gray-200 rounded skeleton w-28"></div>
          </div>
          <div className="h-8 bg-gray-200 rounded skeleton w-20 mb-1"></div>
          <div className="h-3 bg-gray-200 rounded skeleton w-16"></div>
        </div>

        {/* Card 3 - Today stats */}
        <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-4 rounded-lg border border-purple-200">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-4 h-4 bg-gray-200 rounded skeleton"></div>
            <div className="h-4 bg-gray-200 rounded skeleton w-20"></div>
          </div>
          <div className="h-8 bg-gray-200 rounded skeleton w-12 mb-1"></div>
          <div className="h-3 bg-gray-200 rounded skeleton w-16"></div>
        </div>

        {/* Card 4 - Rating */}
        <div className="bg-gradient-to-r from-yellow-50 to-orange-50 p-4 rounded-lg border border-yellow-200">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-4 h-4 bg-gray-200 rounded skeleton"></div>
            <div className="h-4 bg-gray-200 rounded skeleton w-28"></div>
          </div>
          <div className="h-8 bg-gray-200 rounded skeleton w-20 mb-1"></div>
          <div className="h-3 bg-gray-200 rounded skeleton w-24"></div>
        </div>
      </div>

      {/* Footer skeleton */}
      <div className="mt-4 pt-4 border-t border-gray-200">
        <div className="flex justify-between items-center">
          <div className="h-4 bg-gray-200 rounded skeleton w-28"></div>
          <div className="h-3 bg-gray-200 rounded skeleton w-32"></div>
        </div>
      </div>
    </div>
  );
};

const StatsWidget = () => {
  const [stats, setStats] = useState({
    onlineUsers: 0,
    keyReplacements: 0,
    dailyStats: {},
    ratingStats: null
  });
  const [isLoading, setIsLoading] = useState(true);
  const [showRatingModal, setShowRatingModal] = useState(false);

  useEffect(() => {
    setIsLoading(true);

    // Подписываемся на количество пользователей онлайн
    const unsubscribeUsers = chatService.subscribeToUserCount((count) => {
      setStats(prev => ({ ...prev, onlineUsers: count }));
    });

    // Подписываемся на статистику замененных ключей
    const unsubscribeStats = chatService.subscribeToKeyReplacementsStats((data) => {
      setStats(prev => ({ 
        ...prev, 
        keyReplacements: data.total,
        dailyStats: data.daily
      }));
    });

    // Подписываемся на статистику рейтингов
    const unsubscribeRatings = chatService.subscribeToRatingStats((data) => {
      setStats(prev => ({ 
        ...prev, 
        ratingStats: data
      }));
    });

    setIsLoading(false);

    return () => {
      unsubscribeUsers();
      unsubscribeStats();
      unsubscribeRatings();
    };
  }, []);

  // Получить статистику за сегодня
  const getTodayStats = () => {
    const today = new Date().toISOString().split('T')[0];
    return stats.dailyStats[today] || 0;
  };

  // Получить статистику за вчера
  const getYesterdayStats = () => {
    const yesterday = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString().split('T')[0];
    return stats.dailyStats[yesterday] || 0;
  };

  if (isLoading) {
    return <StatsWidgetSkeleton />;
  }

  return (
    <div className="mt-4 bg-white rounded-lg shadow-lg p-6 animate-fade-in-up content-reveal">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
          <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 24 24">
            <path d="M16 6l2.29 2.29-4.88 4.88-4-4L2 16.59 3.41 18l6-6 4 4 6.3-6.29L22 12V6z"/>
          </svg>
        </div>
        <h3 className="text-lg font-semibold text-gray-800">Статистика сервиса</h3>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Онлайн пользователи */}
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-4 rounded-lg border border-green-200 animate-slide-up stagger-1">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
            <span className="text-sm font-medium text-green-700">Онлайн сейчас</span>
          </div>
          <div className="text-2xl font-bold text-green-600">{stats.onlineUsers}</div>
          <div className="text-xs text-green-600">пользователей</div>
        </div>

        {/* Всего заменено ключей */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-4 rounded-lg border border-blue-200 animate-slide-up stagger-2">
          <div className="flex items-center gap-2 mb-2">
            <svg className="w-4 h-4 text-blue-600" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
            </svg>
            <span className="text-sm font-medium text-blue-700">Всего заменено</span>
          </div>
          <div className="text-2xl font-bold text-blue-600">{stats.keyReplacements}</div>
          <div className="text-xs text-blue-600">ключей</div>
        </div>

        {/* Заменено сегодня */}
        <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-4 rounded-lg border border-purple-200 animate-slide-up stagger-3">
          <div className="flex items-center gap-2 mb-2">
            <svg className="w-4 h-4 text-purple-600" fill="currentColor" viewBox="0 0 24 24">
              <path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM7 10h5v5H7z"/>
            </svg>
            <span className="text-sm font-medium text-purple-700">Сегодня</span>
          </div>
          <div className="text-2xl font-bold text-purple-600">{getTodayStats()}</div>
          <div className="text-xs text-purple-600">замен</div>
        </div>

        {/* Средняя оценка - Кликабельная */}
        <button
          onClick={() => setShowRatingModal(true)}
          className="bg-gradient-to-r from-yellow-50 to-orange-50 p-4 rounded-lg border border-yellow-200 animate-slide-up stagger-4 hover:border-yellow-400 hover:shadow-md transition-all duration-200 text-left w-full group"
        >
          <div className="flex items-center gap-2 mb-2">
            <svg className="w-4 h-4 text-yellow-600 group-hover:scale-110 transition-transform" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
            </svg>
            <span className="text-sm font-medium text-yellow-700">Средняя оценка</span>
            <svg className="w-3 h-3 text-yellow-500 ml-auto opacity-0 group-hover:opacity-100 transition-opacity" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </div>
          <div className="flex items-baseline gap-1">
            <div className="text-2xl font-bold text-yellow-600">
              {stats.ratingStats ? parseFloat(stats.ratingStats.average).toFixed(1) : '0.0'}
            </div>
            <div className="text-sm text-yellow-500">/5</div>
          </div>
          <div className="text-xs text-yellow-600">
            {stats.ratingStats ? `${stats.ratingStats.count} отзывов` : 'нет отзывов'}
          </div>
          <div className="text-xs text-yellow-500 mt-1 opacity-0 group-hover:opacity-100 transition-opacity">
            👆 Нажмите для деталей
          </div>
        </button>
      </div>

      {/* Дополнительная информация */}
      <div className="mt-4 pt-4 border-t border-gray-200">
        <div className="flex justify-between items-center text-sm text-gray-600">
          <span>Вчера: {getYesterdayStats()} замен</span>
          <span className="text-xs text-gray-500">
            Обновлено: {new Date().toLocaleTimeString('ru-RU')}
          </span>
        </div>
      </div>

      {/* Модальное окно с деталями рейтинга */}
      <RatingDetailsModal 
        isOpen={showRatingModal}
        onClose={() => setShowRatingModal(false)}
      />
    </div>
  );
};

export default StatsWidget;
